import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Clock,
  DollarSign,
  ExternalLink,
  Eye,
  Globe,
  Heart,
  Info,
  Loader2,
  MousePointerClick,
  Percent,
  RefreshCw,
  Search,
  ShoppingCart,
  TrendingUp,
  Users,
  Video,
  X,
} from "lucide-react";
import AppShell from "../components/AppShell";
import ExportButton from "../components/ExportButton";
import { getAccountDisplayTitle } from "../shared/utils/account-display";
import {
  accountsForTrend,
  buildInsights,
  buildKpis,
  buildTodos,
  buildTrend,
  filterByAccount,
  formatKpiValue,
  groupAccounts,
  hydrateAccounts,
  kpiTooltip,
  resolveRange,
  syncWindowForRange,
  tabContentState,
  TABS,
  TREND_METRICS,
} from "../shared/utils/overview";
import ApiService from "../services/ApiService";
import InsightsService from "../services/InsightsService";
import IntegrationsService from "../services/IntegrationsService";
import { cn } from "../components/ui/cn";

const LINE_COLORS = ["#6366F1", "#0EA5E9", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6", "#64748B"];
const KPI_ICONS = {
  visitors: Users,
  reach: Eye,
  adSpend: DollarSign,
  conversions: ShoppingCart,
  followers: Users,
  spend: DollarSign,
  impressions: Eye,
  clicks: MousePointerClick,
  ctr: Percent,
  cpc: DollarSign,
  costPerConversion: DollarSign,
  roas: TrendingUp,
  views: Eye,
  engagements: Heart,
  users: Users,
  sessions: MousePointerClick,
  pageViews: Globe,
  bounceRate: Percent,
  averageSessionDuration: Clock,
  position: Search,
};
const ACCENTS = {
  indigo: "text-indigo-600 bg-indigo-50",
  sky: "text-sky-600 bg-sky-50",
  pink: "text-pink-600 bg-pink-50",
  emerald: "text-emerald-600 bg-emerald-50",
  violet: "text-violet-600 bg-violet-50",
  amber: "text-amber-600 bg-amber-50",
  rose: "text-rose-600 bg-rose-50",
  orange: "text-orange-600 bg-orange-50",
};
const EMPTY_COPY = {
  summary: {
    title: "No channels connected yet",
    body: "Connect ads, social accounts, your website, or Search Console to see performance in one place.",
  },
  ads: {
    title: "No ad accounts connected",
    body: "Connect Meta or Google Ads to see spend, clicks, campaigns, and return on ad spend.",
  },
  social: {
    title: "No social accounts connected",
    body: "Connect a Facebook Page or Instagram account to see followers, reach, and top posts.",
  },
  website: {
    title: "No website connected",
    body: "Connect Google Analytics to see visitors, sessions, channels, and top pages.",
  },
  search: {
    title: "No search property connected",
    body: "Connect Google Search Console to see queries, clicks, and average position.",
  },
};

function rangeLabel(range) {
  if (range.invalid) return "Choose a valid date range";
  if (range.preset === "7d") return "Last 7 days";
  if (range.preset === "30d") return "Last 30 days";
  if (range.preset === "90d") return "Last 90 days";
  return `${range.start} – ${range.end}`;
}

function kpiGridClass(count) {
  if (count >= 8) return "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4";
  if (count === 6) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
  if (count === 5) return "grid-cols-1 sm:grid-cols-2 xl:grid-cols-5";
  return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";
}

function changeClass(stat) {
  if (stat.change == null || stat.change === 0 || stat.tone === "neutral") return "text-slate-500";
  const increased = stat.change > 0;
  const improved = stat.tone === "lower-better" ? !increased : increased;
  return improved ? "text-emerald-600" : "text-rose-600";
}

function fmtDateTime(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function PrimaryLink({ to, children }) {
  return (
    <RouterLink to={to} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700">
      {children}
    </RouterLink>
  );
}

function SecondaryButton({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
    >
      {children}
    </button>
  );
}

function KpiCard({ stat, range }) {
  const Icon = KPI_ICONS[stat.key] || TrendingUp;
  const tip = kpiTooltip(stat, range);
  const ChangeIcon = stat.change > 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="analytics-kpi">
      <div className="flex items-start gap-3">
        <div className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl", ACCENTS[stat.accent] || ACCENTS.indigo)}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium text-slate-500">{stat.label}</p>
            <button
              type="button"
              className="rounded p-0.5 text-slate-400 hover:text-slate-600"
              title={tip}
              aria-label={tip}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="mt-0.5 text-2xl font-bold tracking-tight text-slate-900">{formatKpiValue(stat)}</p>
          {stat.change == null ? (
            <p className="mt-1 text-xs text-slate-400">No prior period</p>
          ) : (
            <p className={cn("mt-1 inline-flex items-center gap-0.5 text-xs font-semibold", changeClass(stat))}>
              {stat.change !== 0 && <ChangeIcon className="h-3.5 w-3.5" />}
              {stat.change > 0 ? "+" : ""}
              {stat.change.toFixed(1)}%
              <span className="font-medium text-slate-400"> vs previous</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Toast({ snackbar, onClose }) {
  if (!snackbar) return null;
  const styles = { success: "bg-emerald-600", warning: "bg-amber-500", error: "bg-red-600" };
  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-sm">
      <div className={cn("flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white shadow-lg", styles[snackbar.severity] || styles.success)}>
        <span className="flex-1">{snackbar.message}</span>
        <button type="button" onClick={onClose} className="rounded p-0.5 hover:bg-white/20" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function DashboardView() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [data, setData] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [preset, setPreset] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [tab, setTab] = useState("summary");
  const [filters, setFilters] = useState({
    summary: "all", ads: "all", social: "all", website: "all", search: "all",
  });
  const [trendMetric, setTrendMetric] = useState({
    summary: "visitors", ads: "spend", social: "reach", website: "users", search: "clicks",
  });
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState(null);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const [me, dashboard, connected] = await Promise.all([
        ApiService.getAccountMe(),
        InsightsService.getDashboard(),
        IntegrationsService.listConnected({ includePaused: true }).catch(() => []),
      ]);
      setAccount(me?.account ?? null);
      setData(dashboard ?? null);
      setConnections(Array.isArray(connected) ? connected : []);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/signin");
        return;
      }
      setError(e?.response?.data?.message || e.message || "Failed to load overview.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!snackbar) return undefined;
    const timer = setTimeout(() => setSnackbar(null), 4000);
    return () => clearTimeout(timer);
  }, [snackbar]);

  const accounts = useMemo(
    () => hydrateAccounts(Array.isArray(data?.perAccount) ? data.perAccount : [], connections),
    [data, connections],
  );
  const grouped = useMemo(() => groupAccounts(accounts), [accounts]);
  const range = useMemo(
    () => resolveRange({ preset, customStart, customEnd }),
    [preset, customStart, customEnd],
  );
  const syncWindow = syncWindowForRange(range);
  const pools = useMemo(() => ({
    summary: [...grouped.ads, ...grouped.social, ...grouped.website, ...grouped.search],
    ads: grouped.ads,
    social: grouped.social,
    website: grouped.website,
    search: grouped.search,
  }), [grouped]);
  const pool = useMemo(() => pools[tab] || [], [pools, tab]);
  const selectedId = pool.some((entry) => entry.connectedAccount?.id === filters[tab]) ? filters[tab] : "all";
  const filtered = useMemo(
    () => filterByAccount(pool, selectedId),
    [pool, selectedId],
  );
  const kpis = useMemo(() => buildKpis(tab, filtered, range), [tab, filtered, range]);
  const insights = useMemo(
    () => (tab === "summary" ? buildInsights(kpis) : []),
    [tab, kpis],
  );
  const todos = useMemo(
    () => buildTodos(tab === "summary" ? accounts : filtered),
    [tab, accounts, filtered],
  );
  const metric = TREND_METRICS[tab].some((item) => item.key === trendMetric[tab])
    ? trendMetric[tab]
    : TREND_METRICS[tab][0].key;
  const trend = useMemo(() => {
    const source = filterByAccount(accountsForTrend(grouped, tab, metric), selectedId);
    return buildTrend(source, metric, range);
  }, [grouped, tab, metric, selectedId, range]);
  const state = tabContentState(filtered);
  const metaAds = filtered.filter((entry) => entry.connectedAccount?.providerAccountKind === "facebook_ads_account");
  const campaignKey = metaAds.map((entry) => entry.connectedAccount.id).join(",");

  useEffect(() => {
    if (tab !== "ads") return undefined;
    if (!metaAds.length) {
      setCampaigns([]);
      setCampaignsError(null);
      return undefined;
    }
    let cancelled = false;
    setCampaignsLoading(true);
    setCampaignsError(null);
    (async () => {
      const settled = await Promise.allSettled(metaAds.map(async (entry) => {
        const rows = await InsightsService.getCampaigns(entry.connectedAccount.id, syncWindow);
        return (Array.isArray(rows) ? rows : []).map((row) => ({
          ...row,
          accountName: entry.connectedAccount.displayName || "Ad account",
          accountId: entry.connectedAccount.id,
        }));
      }));
      if (cancelled) return;
      const failures = settled.filter((item) => item.status === "rejected");
      const rows = settled
        .filter((item) => item.status === "fulfilled")
        .flatMap((item) => item.value)
        .sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0))
        .slice(0, 25);
      setCampaigns(rows);
      setCampaignsError(rows.length === 0 && failures.length
        ? (failures[0].reason?.response?.data?.message || failures[0].reason?.message || "Couldn't load campaigns.")
        : null);
      setCampaignsLoading(false);
    })();
    return () => { cancelled = true; };
    // metaAds is derived from campaignKey + filtered data already represented by campaignKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, campaignKey, syncWindow]);

  async function handleSyncAll() {
    setRefreshing(true);
    try {
      const result = await InsightsService.refreshAll(syncWindow);
      const failed = (result || []).filter((row) => !row.ok);
      setSnackbar(failed.length
        ? { severity: "warning", message: `Synced with ${failed.length} error(s).` }
        : { severity: "success", message: "Sync finished." });
      await fetchData();
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Sync failed." });
    } finally {
      setRefreshing(false);
    }
  }

  async function handleSignOut() {
    try { await ApiService.signout(); } catch { /* ignore */ }
    navigate("/signin");
  }

  function choosePreset(next) {
    if (next === "custom") {
      const current = resolveRange({ preset: preset === "custom" ? "30d" : preset });
      setCustomStart((value) => value || current.start);
      setCustomEnd((value) => value || current.end);
    }
    setPreset(next);
  }

  const pageWarnings = filtered.filter(
    (entry) => (
      entry.connectedAccount?.providerAccountKind === "facebook_page"
      || entry.connectedAccount?.providerAccountKind === "youtube_channel"
    ) && entry.metrics?.warning,
  );
  const youtubeAccounts = filtered.filter(
    (entry) => entry.connectedAccount?.providerAccountKind === "youtube_channel",
  );
  const topPosts = filtered
    .flatMap((entry) => (entry.topPosts || []).map((post) => ({ ...post, _account: entry.connectedAccount })))
    .sort((a, b) => (b.engagedUsers || 0) + (b.likes || 0) - ((a.engagedUsers || 0) + (a.likes || 0)))
    .slice(0, 9);
  const channels = useMemo(() => {
    const totals = [];
    filtered.forEach((entry) => {
      (entry.topChannels || []).forEach((row) => {
        if (!row?.channel) return;
        const existing = totals.find((item) => item.channel === row.channel);
        if (existing) {
          existing.sessions += Number(row.sessions || 0);
          existing.conversions += Number(row.conversions || 0);
        } else {
          totals.push({
            channel: row.channel,
            sessions: Number(row.sessions || 0),
            conversions: Number(row.conversions || 0),
          });
        }
      });
    });
    return totals.sort((a, b) => b.sessions - a.sessions).slice(0, 8);
  }, [filtered]);
  const searchQueries = filtered.flatMap((entry) => (entry.topChannels || [])
    .filter((row) => row?.query)
    .map((row) => ({ ...row, account: entry.connectedAccount?.displayName })))
    .sort((a, b) => Number(b.clicks || 0) - Number(a.clicks || 0))
    .slice(0, 10);
  const searchPages = filtered.flatMap((entry) => (entry.topPosts || []).map((row) => ({
    page: row.message || row.permalink,
    clicks: row.engagedUsers,
    impressions: row.impressions,
    account: entry.connectedAccount?.displayName,
  }))).slice(0, 10);
  const webPages = filtered.flatMap((entry) => (entry.topPosts || []).map((row) => ({
    title: row.message || row.permalink || "Page",
    path: row.permalink || row.id,
    views: row.impressions,
    sessions: row.engagedUsers,
  }))).sort((a, b) => Number(b.views || 0) - Number(a.views || 0)).slice(0, 10);

  const headerActions = (
    <button
      type="button"
      onClick={handleSyncAll}
      disabled={refreshing || accounts.length === 0}
      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {refreshing ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <RefreshCw className="h-4 w-4 text-slate-500" />}
      {refreshing ? "Syncing…" : "Sync all"}
    </button>
  );

  const period = rangeLabel(range);
  const showMetrics = state === "ready";

  return (
    <AppShell account={account} onSignOut={handleSignOut} title="Overview" actions={headerActions}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">
              {account?.displayName ? `Welcome back, ${account.displayName.split(" ")[0]}` : "Overview"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Cross-channel performance · {period.toLowerCase()}
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:items-end">
            <div role="radiogroup" aria-label="Date range" className="inline-flex w-fit rounded-xl border border-slate-200 bg-slate-100/80 p-1">
              {[
                ["7d", "7d"],
                ["30d", "30d"],
                ["90d", "90d"],
                ["custom", "Custom"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={preset === value}
                  onClick={() => choosePreset(value)}
                  className={cn("analytics-tab", preset === value ? "analytics-tab-active" : "analytics-tab-inactive")}
                >
                  {label}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs font-medium text-slate-500">
                  From
                  <input
                    type="date"
                    value={customStart}
                    max={customEnd || undefined}
                    onChange={(event) => setCustomStart(event.target.value)}
                    className="ml-2 rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-800"
                  />
                </label>
                <label className="text-xs font-medium text-slate-500">
                  To
                  <input
                    type="date"
                    value={customEnd}
                    min={customStart || undefined}
                    onChange={(event) => setCustomEnd(event.target.value)}
                    className="ml-2 rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-800"
                  />
                </label>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700" aria-label="Dismiss error">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div role="tablist" aria-label="Overview sections" className="flex flex-wrap gap-1">
            {TABS.map((item) => {
              const count = item.id === "summary" ? pools.summary.length : pools[item.id].length;
              const selected = tab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  id={`overview-tab-${item.id}`}
                  aria-selected={selected}
                  aria-controls={`overview-panel-${item.id}`}
                  onClick={() => setTab(item.id)}
                  className={cn(
                    "inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition",
                    selected ? "bg-white text-brand shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:bg-white/70 hover:text-slate-800",
                  )}
                >
                  {item.label}
                  {item.id !== "summary" && (
                    <span className={cn("rounded-full px-1.5 text-xs", selected ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-500")}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {pool.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-slate-500">
              Account
              <select
                aria-label={`${TABS.find((item) => item.id === tab)?.label || "Tab"} account`}
                value={selectedId}
                onChange={(event) => setFilters((prev) => ({ ...prev, [tab]: event.target.value }))}
                className="max-w-[220px] rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 shadow-sm"
              >
                <option value="all">All accounts ({pool.length})</option>
                {pool.map((entry) => (
                  <option key={entry.connectedAccount.id} value={entry.connectedAccount.id}>
                    {entry.connectedAccount.displayName || "Account"}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div role="tabpanel" id={`overview-panel-${tab}`} aria-labelledby={`overview-tab-${tab}`} className="flex flex-col gap-6">
          {loading && !data ? (
            <div className={cn("grid gap-4", kpiGridClass(tab === "ads" ? 8 : 4))}>
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className="analytics-card h-28 animate-pulse bg-slate-100" />
              ))}
            </div>
          ) : !showMetrics ? (
            <StatePanel
              tab={tab}
              state={state}
              refreshing={refreshing}
              onSync={handleSyncAll}
            />
          ) : (
            <>
              {pageWarnings.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {pageWarnings.map((entry) => (
                    <span
                      key={entry.connectedAccount.id}
                      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-950"
                      title={entry.metrics.warning}
                    >
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{entry.connectedAccount.displayName || "Page"}: {entry.metrics.warning}</span>
                    </span>
                  ))}
                </div>
              )}

              {todos.length > 0 && tab !== "summary" && (
                <TodoList items={todos} onSync={handleSyncAll} refreshing={refreshing} framed />
              )}

              {!kpis.some((stat) => stat.current != null) && (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  No daily rows in this date range. Sync a window that covers it.
                  <button type="button" onClick={handleSyncAll} disabled={refreshing} className="ml-2 font-semibold text-indigo-600 hover:text-indigo-700">
                    Sync now
                  </button>
                </div>
              )}

              <div className={cn("grid gap-4", kpiGridClass(kpis.length))}>
                {kpis.map((stat) => (
                  <KpiCard key={stat.key} stat={stat} range={range} />
                ))}
              </div>

              {tab === "summary" && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <section className="analytics-card p-5">
                    <h3 className="text-base font-semibold text-slate-900">What changed</h3>
                    {insights.length === 0 ? (
                      <p className="mt-3 text-sm text-slate-500">Sync your accounts to see a short read on this period.</p>
                    ) : (
                      <ul className="mt-3 space-y-2">
                        {insights.map((line) => (
                          <li key={line} className="text-sm leading-relaxed text-slate-700">{line}</li>
                        ))}
                      </ul>
                    )}
                  </section>
                  <TodoList items={todos} onSync={handleSyncAll} refreshing={refreshing} framed />
                </div>
              )}

              <TrendCard
                metric={metric}
                options={TREND_METRICS[tab]}
                onMetric={(key) => setTrendMetric((prev) => ({ ...prev, [tab]: key }))}
                trend={trend}
                period={period}
                title={tab === "ads" && metric === "spend" ? "Spend trend" : "Performance trend"}
                onSync={handleSyncAll}
                refreshing={refreshing}
              />

              {tab === "ads" && (
                <CampaignTable
                  rows={campaigns}
                  loading={campaignsLoading}
                  error={campaignsError}
                  metaCount={metaAds.length}
                  windowLabel={preset === "custom" ? "last 30 days when the custom range is shorter than 90 days" : period.toLowerCase()}
                />
              )}

              {tab === "social" && (
                <>
                  {youtubeAccounts.map((entry) => (
                    <YouTubeChannelCard
                      key={entry.connectedAccount.id}
                      entry={entry}
                      syncWindow={syncWindow}
                    />
                  ))}
                  <PostGrid posts={topPosts} />
                </>
              )}

              {tab === "website" && (
                <>
                  <ChannelsChart channels={channels} />
                  <PagesTable rows={webPages} />
                </>
              )}

              {tab === "search" && <SearchTables queries={searchQueries} pages={searchPages} />}
            </>
          )}
        </div>
      </div>
      <Toast snackbar={snackbar} onClose={() => setSnackbar(null)} />
    </AppShell>
  );
}

function StatePanel({ tab, state, refreshing, onSync }) {
  const copy = EMPTY_COPY[tab] || EMPTY_COPY.summary;
  const title = state === "error" ? "Connection needs attention" : state === "unsynced" ? "Nothing synced yet" : copy.title;
  const body = state === "error"
    ? "One or more accounts in this tab can't be read. Reconnect them, then sync again."
    : state === "unsynced"
      ? "These accounts are connected, but they don't have metrics yet."
      : copy.body;
  return (
    <div className="analytics-card border-dashed border-indigo-200 bg-gradient-to-br from-slate-50 to-indigo-50/40 px-6 py-14 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-100">
        {state === "error" ? <AlertTriangle className="h-7 w-7 text-amber-600" /> : <Users className="h-7 w-7 text-indigo-600" />}
      </div>
      <h2 className="text-xl font-bold text-slate-900">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">{body}</p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        {state === "empty" && (
          <PrimaryLink to="/integrations">
            Connect channels
            <ArrowRight className="h-4 w-4" />
          </PrimaryLink>
        )}
        {state === "error" && (
          <PrimaryLink to="/integrations">Fix connection</PrimaryLink>
        )}
        {state !== "empty" && (
          <SecondaryButton onClick={onSync} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {refreshing ? "Syncing…" : "Sync now"}
          </SecondaryButton>
        )}
      </div>
    </div>
  );
}

function TodoList({ items, onSync, refreshing, framed = false }) {
  const body = items.length === 0 ? (
    <p className="mt-3 text-sm text-slate-500">Nothing needs attention right now.</p>
  ) : (
    <ul className="mt-3 space-y-3">
      {items.map((item) => (
        <li key={item.id} className="flex flex-col gap-2 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className={cn("text-sm font-semibold", item.tone === "error" ? "text-rose-700" : item.tone === "warning" ? "text-amber-800" : "text-slate-800")}>
              {item.title}
            </p>
            <p className="text-xs leading-relaxed text-slate-500">{item.detail}</p>
          </div>
          {item.action === "fix" ? (
            <RouterLink to="/integrations" className="shrink-0 text-xs font-semibold text-indigo-600 hover:text-indigo-700">
              Fix connection
            </RouterLink>
          ) : (
            <button type="button" onClick={onSync} disabled={refreshing} className="shrink-0 text-xs font-semibold text-indigo-600 hover:text-indigo-700 disabled:opacity-60">
              Sync now
            </button>
          )}
        </li>
      ))}
    </ul>
  );
  if (!framed) return body;
  return (
    <section className="analytics-card p-5">
      <h3 className="text-base font-semibold text-slate-900">To do</h3>
      {body}
    </section>
  );
}

function TrendCard({
  metric, options, onMetric, trend, period, title, onSync, refreshing,
}) {
  const dates = trend.dates.map((day) => new Date(`${day}T00:00:00`));
  return (
    <section className="analytics-card overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <p className="mt-0.5 text-sm text-slate-500">{period} · daily</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onMetric(option.key)}
              className={cn(
                "rounded-lg px-2.5 py-1 text-xs font-semibold transition",
                metric === option.key
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-2 pb-4 pt-2 sm:px-4">
        {trend.lines.length === 0 ? (
          <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500">No daily points for this metric in the selected range.</p>
            <SecondaryButton onClick={onSync} disabled={refreshing}>
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync now
            </SecondaryButton>
          </div>
        ) : (
          <LineChart
            height={280}
            xAxis={[{ data: dates, scaleType: "time" }]}
            series={trend.lines.map((line, index) => ({
              data: line.values,
              label: line.label,
              color: LINE_COLORS[index % LINE_COLORS.length],
              curve: "monotoneX",
              showMark: false,
              connectNulls: true,
              area: trend.lines.length === 1,
            }))}
            margin={{ left: 56, right: 16, top: 16, bottom: 32 }}
            sx={{
              ".MuiAreaElement-root": { fillOpacity: 0.08 },
              ".MuiLineElement-root": { strokeWidth: 2.5 },
            }}
          />
        )}
      </div>
    </section>
  );
}

function CampaignTable({
  rows, loading, error, metaCount, windowLabel,
}) {
  return (
    <section className="analytics-card overflow-hidden">
      <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Campaigns</h3>
          <p className="mt-0.5 text-xs text-slate-500">Meta ad campaigns · {windowLabel}</p>
        </div>
        {rows.length > 0 && (
          <ExportButton
            rows={rows}
            filename="ad-campaigns.csv"
            columns={[
              { label: "Account", value: (row) => row.accountName },
              { label: "Campaign", value: (row) => row.campaignName },
              { label: "Spend", value: (row) => row.spend },
              { label: "Impressions", value: (row) => row.impressions },
              { label: "Clicks", value: (row) => row.clicks },
              { label: "CTR", value: (row) => row.ctr },
              { label: "CPC", value: (row) => row.cpc },
              { label: "Conversions", value: (row) => row.conversions },
              { label: "ROAS", value: (row) => row.roas },
            ]}
          />
        )}
      </div>
      {metaCount === 0 ? (
        <p className="px-5 py-8 text-sm text-slate-500">Campaign breakdown is available for Meta ad accounts. Google Ads totals are in the cards above.</p>
      ) : loading ? (
        <div className="flex items-center gap-2 px-5 py-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading campaigns…
        </div>
      ) : error ? (
        <p className="px-5 py-8 text-sm text-rose-700">{error}</p>
      ) : rows.length === 0 ? (
        <p className="px-5 py-8 text-sm text-slate-500">No campaigns in this range. Sync the ad account and try again.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-400">
                {["Campaign", "Spend", "Impressions", "Clicks", "CTR", "CPC", "Conv.", "ROAS"].map((label) => (
                  <th key={label} className={cn("px-4 py-3", label !== "Campaign" && "text-right")}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={`${row.accountId}-${row.campaignId}`} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{row.campaignName || row.campaignId}</p>
                    <p className="text-xs text-slate-400">{row.accountName}</p>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatKpiValue({ format: "money", current: row.spend })}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatKpiValue({ current: row.impressions })}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatKpiValue({ current: row.clicks })}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatKpiValue({ format: "percent", current: row.ctr })}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatKpiValue({ format: "money", current: row.cpc })}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatKpiValue({ current: row.conversions })}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatKpiValue({ format: "roas", current: row.roas })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function fmtWatch(minutes) {
  const value = Number(minutes || 0);
  if (value >= 60) return `${(value / 60).toFixed(1)}h`;
  return `${Math.round(value)}m`;
}

function YouTubeChannelCard({ entry, syncWindow }) {
  const [pane, setPane] = useState("channel");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const metrics = entry.metrics || {};
  const byType = metrics.byContentType || {};
  const shorts = byType.SHORTS || {};
  const longForm = byType.VIDEO_ON_DEMAND || {};
  const topShorts = metrics.topShorts || entry.topChannels || [];
  const account = entry.connectedAccount || {};

  const openVideo = async (videoId) => {
    if (!videoId) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const data = await InsightsService.getYouTubeVideoDetail(account.id, videoId, syncWindow);
      setDetail(data);
    } catch (err) {
      setDetailError(err?.response?.data?.message || err?.message || "Could not load video detail");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const panes = [
    { id: "channel", label: "Channel" },
    { id: "videos", label: "Videos" },
    { id: "shorts", label: "Shorts" },
  ];

  const channelStats = [
    { label: "Subscribers", value: formatKpiValue({ current: metrics.subscribers ?? metrics.followers }) },
    { label: "Views", value: formatKpiValue({ current: metrics.views }) },
    { label: "Watch time", value: fmtWatch(metrics.estimatedMinutesWatched) },
    { label: "Avg view", value: `${Math.round(Number(metrics.averageViewDuration || 0))}s` },
  ];
  const videoStats = [
    { label: "Views", value: formatKpiValue({ current: longForm.views }) },
    { label: "Watch time", value: fmtWatch(longForm.estimatedMinutesWatched) },
    { label: "Avg view", value: `${Math.round(Number(longForm.averageViewDuration || 0))}s` },
    { label: "Likes", value: formatKpiValue({ current: longForm.likes }) },
  ];
  const shortStats = [
    { label: "Views", value: formatKpiValue({ current: shorts.views }) },
    { label: "Watch time", value: fmtWatch(shorts.estimatedMinutesWatched) },
    { label: "Subs gained", value: formatKpiValue({ current: shorts.subscribersGained }) },
    { label: "Likes", value: formatKpiValue({ current: shorts.likes }) },
  ];

  const stats = pane === "videos" ? videoStats : pane === "shorts" ? shortStats : channelStats;
  const list = pane === "shorts" ? topShorts : (entry.topPosts || []);

  return (
    <section className="analytics-card overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {account.avatarUrl ? (
            <img src={account.avatarUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
              <Video className="h-5 w-5" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-slate-900">{account.displayName || "YouTube"}</h3>
            <p className="text-xs text-slate-500">YouTube · Channel / Videos / Shorts</p>
          </div>
        </div>
        <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs font-semibold">
          {panes.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setPane(item.id)}
              className={cn(
                "rounded-md px-3 py-1.5",
                pane === item.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {metrics.warning && (
        <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{metrics.warning}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{stat.label}</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900">{stat.value}</p>
          </div>
        ))}
      </div>

      {pane !== "channel" && (
        <div className="border-t border-slate-100 px-5 py-4">
          <h4 className="text-sm font-semibold text-slate-900">{pane === "shorts" ? "Top Shorts" : "Top videos"}</h4>
          {list.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">No {pane === "shorts" ? "Shorts" : "videos"} in this window.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {list.slice(0, 5).map((item) => (
                <li key={item.id || item.name} className="flex items-center gap-3 rounded-lg border border-slate-100 p-2">
                  <div className="h-12 w-20 shrink-0 overflow-hidden rounded bg-slate-100">
                    {(item.thumbnailUrl) ? (
                      <img src={item.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <button type="button" onClick={() => openVideo(item.id)} className="truncate text-left text-sm font-medium text-slate-800 hover:text-indigo-600">
                      {item.title || item.message || item.name || item.id}
                    </button>
                    <p className="text-xs text-slate-500">
                      {formatKpiValue({ current: item.views })} views
                      {item.estimatedMinutesWatched != null ? ` · ${fmtWatch(item.estimatedMinutesWatched)} watch` : ""}
                    </p>
                  </div>
                  {item.permalink && (
                    <a href={item.permalink} target="_blank" rel="noreferrer" className="text-indigo-600" aria-label="Open on YouTube">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {pane === "channel" && (
        <div className="grid gap-4 border-t border-slate-100 px-5 py-4 lg:grid-cols-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Traffic sources</h4>
            {(metrics.trafficSources || []).length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">No traffic source data yet.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {(metrics.trafficSources || []).slice(0, 6).map((row) => (
                  <li key={row.source} className="flex justify-between gap-3">
                    <span className="truncate">{row.source}</span>
                    <span className="tabular-nums text-slate-500">{formatKpiValue({ current: row.views })}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Audience by country</h4>
            {(metrics.audience?.countries || []).length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">No audience data yet.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {(metrics.audience.countries || []).slice(0, 6).map((row) => (
                  <li key={row.country} className="flex justify-between gap-3">
                    <span>{row.country}</span>
                    <span className="tabular-nums text-slate-500">{Number(row.viewerPercentage || 0).toFixed(1)}%</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {metrics.unavailable && (
            <p className="text-xs text-slate-400 lg:col-span-2">
              Studio-only (not in public API): impressions, impressions CTR, Shorts viewed vs swiped away, viewer language.
            </p>
          )}
        </div>
      )}

      {(detailLoading || detail || detailError) && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-4">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-900">Video detail</h4>
            <button type="button" className="text-xs text-slate-500" onClick={() => { setDetail(null); setDetailError(null); }}>Close</button>
          </div>
          {detailLoading && <p className="text-sm text-slate-500">Loading retention and traffic…</p>}
          {detailError && <p className="text-sm text-rose-600">{detailError}</p>}
          {detail && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-800">{detail.video?.title}</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 text-xs">
                <div>Views <strong className="block text-sm">{formatKpiValue({ current: detail.metrics?.views })}</strong></div>
                <div>Watch <strong className="block text-sm">{fmtWatch(detail.metrics?.estimatedMinutesWatched)}</strong></div>
                <div>Avg <strong className="block text-sm">{Math.round(Number(detail.metrics?.averageViewDuration || 0))}s</strong></div>
                <div>Likes <strong className="block text-sm">{formatKpiValue({ current: detail.metrics?.likes })}</strong></div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Retention</p>
                {(detail.retention || []).length === 0 ? (
                  <p className="mt-1 text-sm text-slate-500">No retention points.</p>
                ) : (
                  <p className="mt-1 text-sm text-slate-600">
                    {(detail.retention || []).length} points · start {(Number(detail.retention[0]?.audienceWatchRatio || 0) * 100).toFixed(0)}%
                    → end {(Number(detail.retention[detail.retention.length - 1]?.audienceWatchRatio || 0) * 100).toFixed(0)}%
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Traffic sources</p>
                <ul className="mt-1 space-y-1 text-sm text-slate-700">
                  {(detail.trafficSources || []).slice(0, 5).map((row) => (
                    <li key={row.source} className="flex justify-between gap-3">
                      <span className="truncate">{row.source}</span>
                      <span className="tabular-nums text-slate-500">{formatKpiValue({ current: row.views })}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PostGrid({ posts }) {
  return (
    <section className="analytics-card overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-4">
        <h3 className="text-base font-semibold text-slate-900">Top posts</h3>
        <p className="mt-0.5 text-xs text-slate-500">Highest engagement in the latest sync</p>
      </div>
      {posts.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-slate-500">No posts yet. Sync a Page, Instagram, or YouTube account to fill this grid.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3">
          {posts.map((post, index) => (
            <article key={`${post._account?.id}-${post.id}-${index}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="aspect-video bg-slate-100">
                {post.thumbnailUrl ? (
                  <img src={post.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-slate-400">No thumbnail</div>
                )}
              </div>
              <div className="space-y-2 p-3">
                <p className="line-clamp-2 text-sm font-medium text-slate-800">{post.message || "No caption"}</p>
                <p className="text-xs text-slate-400">
                  {getAccountDisplayTitle(post._account || {})}
                  {post.createdAt ? ` · ${fmtDateTime(post.createdAt)}` : ""}
                </p>
                <div className="flex items-center justify-between text-xs font-semibold text-slate-600">
                  <span>{post.likes == null ? "—" : formatKpiValue({ current: post.likes })} {post._account?.providerAccountKind === "instagram_business" ? "likes" : "reactions"}</span>
                  <span>{post.comments == null ? "—" : formatKpiValue({ current: post.comments })} comments</span>
                  <span>{post.shares == null ? "—" : formatKpiValue({ current: post.shares })} shares</span>
                  <span>{post.impressions == null ? "—" : formatKpiValue({ current: post.impressions })} views</span>
                  {post.permalink && (
                    <a href={post.permalink.startsWith("http") ? post.permalink : post.permalink} target="_blank" rel="noreferrer" className="text-indigo-600" aria-label="Open post">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function ChannelsChart({ channels }) {
  if (!channels.length) {
    return (
      <section className="analytics-card px-5 py-8 text-sm text-slate-500">
        No channel breakdown in the latest sync.
      </section>
    );
  }
  return (
    <section className="analytics-card overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-4">
        <h3 className="text-base font-semibold text-slate-900">Channels</h3>
        <p className="mt-0.5 text-xs text-slate-500">Sessions and conversions by acquisition channel from the latest sync</p>
      </div>
      <div className="px-2 pb-4 pt-2">
        <BarChart
          height={260}
          xAxis={[{ data: channels.map((row) => row.channel), scaleType: "band" }]}
          series={[
            { data: channels.map((row) => row.sessions), label: "Sessions", color: "#6366F1" },
            { data: channels.map((row) => row.conversions), label: "Conversions", color: "#0EA5E9" },
          ]}
          margin={{ left: 56, right: 16, top: 16, bottom: 72 }}
        />
      </div>
    </section>
  );
}

function PagesTable({ rows }) {
  return (
    <section className="analytics-card overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-4">
        <h3 className="text-base font-semibold text-slate-900">Top pages</h3>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-sm text-slate-500">No pages in the latest sync.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-400">
                <th className="px-5 py-3">Page</th>
                <th className="px-3 py-3 text-right">Views</th>
                <th className="px-5 py-3 text-right">Sessions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.path || row.title}>
                  <td className="px-5 py-3">
                    <p className="font-medium text-slate-800">{row.title}</p>
                    <p className="truncate text-xs text-slate-400">{row.path}</p>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatKpiValue({ current: row.views })}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{formatKpiValue({ current: row.sessions })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SearchTables({ queries, pages }) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <section className="analytics-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="text-base font-semibold text-slate-900">Top queries</h3>
        </div>
        {queries.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500">No queries in the latest sync.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-3">Query</th>
                  <th className="px-3 py-3 text-right">Clicks</th>
                  <th className="px-3 py-3 text-right">Impr.</th>
                  <th className="px-5 py-3 text-right">Pos.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {queries.map((row) => (
                  <tr key={`${row.account}-${row.query}`}>
                    <td className="px-5 py-3 font-medium text-slate-800">{row.query}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatKpiValue({ current: row.clicks })}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatKpiValue({ current: row.impressions })}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{formatKpiValue({ format: "position", current: row.position })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="analytics-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="text-base font-semibold text-slate-900">Top pages</h3>
        </div>
        {pages.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500">No pages in the latest sync.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-3">Page</th>
                  <th className="px-3 py-3 text-right">Clicks</th>
                  <th className="px-5 py-3 text-right">Impr.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pages.map((row) => (
                  <tr key={`${row.account}-${row.page}`}>
                    <td className="max-w-[240px] truncate px-5 py-3 font-medium text-slate-800" title={row.page}>{row.page}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatKpiValue({ current: row.clicks })}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{formatKpiValue({ current: row.impressions })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
